/**
 * vl-hero-slider.js — VL Hero 滑块的交互与播放进度
 *
 * 需求：需求/20260922-新版首页改版/子需求/02-首页Hero滑块/
 * 用户裁决（2026-09-28）：
 *   - 屏数＝block 数（动态）；单图静态展示；单视频自身循环播放但不切屏；单屏均不显示指示条
 *   - 进度起点＝9 点钟方向（左端中点）顺时针；PC 与 M 同一进度语义
 *   - 图片屏按 data-vl-duration（秒，0.1 精度）推进
 *   - 视频屏以视频实际长度为准（`ended` 即切）；无法播放时按图片时长兜底
 *   - 手动切换后进度归零；PC 悬停与页面不可见时暂停
 *   - 主题编辑器（designMode）内不自动播放
 *
 * 进度可视化：PC＝当前屏胶囊的 1.5px 外圈（SVG path + pathLength=100 + stroke-dashoffset）；
 *            M＝当前屏 72×8 进度条的填充宽度。两者由同一个 0→1 进度值驱动。
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || window.VLHeroSlider) return;

  var FALLBACK_MS = 300; // 视频未在此时长内开始播放 → 退化为按时长推进
  var SWIPE_MIN = 40; // 滑动切换阈值（px）
  var DOT_BASE = 8; // 圆点基础宽（与 CSS 的 8×8 一致）
  var PILL_STEP_MS = 200; // bot「逐格交接」每一步（相邻两项之间）的时长，对齐子需求 05 的 bot
  var SVG_SCOPE_UID = 0; // 内联 SVG 的 document 级唯一命名空间，避免 PC/M 及多实例的 id 冲突

  function prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  function isDesignMode() {
    try {
      return !!(window.Shopify && window.Shopify.designMode);
    } catch (e) {
      return false;
    }
  }

  /* 当前断点判断（与全项目一致：≥768 = PC）。
     用途：复用主题 `high-quality-banner-video` 时，一屏会输出 **PC/M 两支 `<video>`**
     （`.media__image--desktop` / `--mobile`），由 CSS 按断点显隐（见本组件 CSS 的显隐补丁）。
     JS 只应播放/读取**显示中的那一支** —— 否则被隐藏的那支也会被 `play()` 拉起来
     （M 端白下 ~3Mbps 的 PC 版、PC 端白下移动版），且 M 端「按视频实际长度推进」会读错视频
     （用户 2026-10-09 反馈「PC 端视频没取对」后一并修正）。 */
  function isPcBreakpoint() {
    try {
      return window.matchMedia('(min-width: 768px)').matches;
    } catch (e) {
      return true;
    }
  }

  class VLHeroSlider extends HTMLElement {
    connectedCallback() {
      try {
        if (this._initialised) return;
        this._initialised = true;

        this._namespaceInlineSvgs();

        this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-vl-slide]'));
        this.items = Array.prototype.slice.call(this.querySelectorAll('[data-vl-item]'));
        this.count = this.slides.length;
        this.index = 0;
        this.progress = 0;
        this._elapsed = 0;
        this._lastTs = 0;
        this._paused = false;
        /* 悬停暂停只对「可悬停 + 精确指针」的设备生效（用户 2026-10-20）：
           触屏上点指示条会合成 mouseenter，若不禁用，`_paused` 会被置 true 且没有 mouseleave 可解，
           导致「手机端点了 bot 后视频暂停、再也没有恢复入口」。手机端不存在暂停方式 ⇒ 永不暂停。 */
        this._hoverPauseEnabled = !!(
          window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches
        );
        this._visible = !document.hidden;
        this._tickBound = this._tick.bind(this);

        // 把「幻灯片轮播」和「单视频自身播放」拆开：
        // · 单图：两者都为 false，静态展示且不读取 duration；
        // · 单视频：只播放当前视频并 loop，不启动进度计时或切屏；
        // · 多屏：沿用原轮播逻辑，视频 ended 后切下一屏。
        var autoplayAllowed =
          this.dataset.vlAutoplay === 'true' && !isDesignMode() && !prefersReducedMotion();
        var onlySlide = this.slides[0];
        this.singleVideoAutoplay = !!(
          autoplayAllowed && this.count === 1 && onlySlide && onlySlide.dataset.vlMedia === 'video'
        );
        this.autoplay = autoplayAllowed && this.count > 1;
        this._videoAutoplayEnabled = this.autoplay || this.singleVideoAutoplay;

        this._onVisibility = this._handleVisibility.bind(this);
        this._onPointerDown = this._handlePointerDown.bind(this);
        this._onPointerUp = this._handlePointerUp.bind(this);
        this._onEnter = this._handleEnter.bind(this);
        this._onLeave = this._handleLeave.bind(this);
        this._onSectionLoad = this._handleSectionLoad.bind(this);
        this._onBlockSelect = this._handleBlockSelect.bind(this);

        document.addEventListener('visibilitychange', this._onVisibility);
        document.addEventListener('shopify:section:load', this._onSectionLoad);
        document.addEventListener('shopify:block:select', this._onBlockSelect);

        /* 悬停暂停的作用域（用户 2026-10-09 口径）：
           只在该「红框」范围内 —— 即指示条的当前项区域（PC＝当前屏 pill、M＝72×8 进度条那一行，
           二者都在 `.vl-hero__items` 里）。此前绑在宿主 `<vl-hero-slider>` 上，鼠标经过 Hero 任意位置
           都会把轮播停住；现在只在指针进入/离开这一行时暂停/恢复（也正好避免"想点 pill 里的 CTA 时
           画面在脚下切换"）。 */
        this._hoverTarget = this.querySelector('.vl-hero__items') || this;
        this._hoverTarget.addEventListener('mouseenter', this._onEnter);
        this._hoverTarget.addEventListener('mouseleave', this._onLeave);
        this.addEventListener('pointerdown', this._onPointerDown);
        this.addEventListener('pointerup', this._onPointerUp);

        /* PC↔M 断点切换：显示中的视频换了 ⇒ 重新对齐播放/暂停（见 _handleBreakpoint） */
        this._onBreakpoint = this._handleBreakpoint.bind(this);
        if (window.matchMedia) {
          this._breakpointMq = window.matchMedia('(min-width: 768px)');
          if (this._breakpointMq.addEventListener) {
            this._breakpointMq.addEventListener('change', this._onBreakpoint);
          } else if (this._breakpointMq.addListener) {
            this._breakpointMq.addListener(this._onBreakpoint); // 老 Safari 兜底
          }
        }

        this._bindControls();
        this._pillPos = this.index;
        this._writePill(this.index); // 首帧先落一次宽度，避免 8px 闪动

        if (this.autoplay) {
          this._rafId = requestAnimationFrame(this._tickBound);
        }

        if (!this.autoplay) {
          // 单视频直接播放并循环；单图或关闭自动播放时只进入第一屏并保持静态。
          this.goTo(0, { silent: !this.singleVideoAutoplay });
        }
      } catch (e) {
        // 异常隔离：组件失败不影响页面其它功能
        this._failed = true;
      }
    }

    disconnectedCallback() {
      try {
        if (this._rafId) cancelAnimationFrame(this._rafId);
        this._rafId = 0;
        document.removeEventListener('visibilitychange', this._onVisibility);
        document.removeEventListener('shopify:section:load', this._onSectionLoad);
        document.removeEventListener('shopify:block:select', this._onBlockSelect);
        if (this._hoverTarget) {
          this._hoverTarget.removeEventListener('mouseenter', this._onEnter);
          this._hoverTarget.removeEventListener('mouseleave', this._onLeave);
          this._hoverTarget = null;
        }
        this.removeEventListener('pointerdown', this._onPointerDown);
        this.removeEventListener('pointerup', this._onPointerUp);
        if (this._breakpointMq) {
          if (this._breakpointMq.removeEventListener) {
            this._breakpointMq.removeEventListener('change', this._onBreakpoint);
          } else if (this._breakpointMq.removeListener) {
            this._breakpointMq.removeListener(this._onBreakpoint);
          }
          this._breakpointMq = null;
        }
        if (this._pillRaf) window.cancelAnimationFrame(this._pillRaf);
        if (this._resizeObserver) this._resizeObserver.disconnect();
        this._resizeObserver = null;
        this._pausedVideos(true);
        this._initialised = false;
      } catch (e) {
        /* noop */
      }
    }

    /* ------------------------------------------------------------- 控制绑定 */

    /* textarea 支持直接填写 SVG。PC/M 文案不同时，两套 SVG 会同时存在于 DOM（仅由 CSS 控制显隐），
       而 SVG 的 gradient/mask/use 都通过 document 级 id 引用；Figma 导出件常重复使用 a、b、paint0 等 id，
       因此隐藏的 PC SVG 也可能劫持手机 SVG 的 href/url(#...)。这里为每一段文案 SVG 建独立命名空间，
       并同步改写所有内部引用；只处理 `.vl-hero__text`，不触碰导航等组件自带 SVG。 */
    _namespaceInlineSvgs() {
      try {
        var svgs = Array.prototype.slice.call(this.querySelectorAll('.vl-hero__text svg'));
        svgs.forEach(function (svg) {
          if (svg.hasAttribute('data-vl-svg-scoped')) return;

          var prefix = 'vlhs-' + ++SVG_SCOPE_UID + '-';
          var idMap = Object.create(null);
          var idNodes = Array.prototype.slice.call(svg.querySelectorAll('[id]'));
          if (svg.hasAttribute('id')) idNodes.unshift(svg);

          idNodes.forEach(function (node) {
            var oldId = node.getAttribute('id');
            if (!oldId) return;
            idMap[oldId] = prefix + oldId;
          });

          idNodes.forEach(function (node) {
            var oldId = node.getAttribute('id');
            if (oldId && idMap[oldId]) node.setAttribute('id', idMap[oldId]);
          });

          [svg].concat(Array.prototype.slice.call(svg.querySelectorAll('*'))).forEach(function (node) {
            Array.prototype.slice.call(node.attributes || []).forEach(function (attr) {
              var value = attr.value;
              var next = value;

              // href / xlink:href 的本地片段引用，例如 #paint0。
              if ((attr.localName === 'href' || attr.name === 'xlink:href') && value.charAt(0) === '#') {
                var hrefId = value.slice(1);
                if (idMap[hrefId]) next = '#' + idMap[hrefId];
              }

              // fill/mask/filter/clip-path/style 等属性中的 url(#paint0)。
              next = next.replace(/url\(\s*(['"]?)#([^)'"\s]+)\1\s*\)/g, function (match, quote, refId) {
                return idMap[refId] ? 'url(' + quote + '#' + idMap[refId] + quote + ')' : match;
              });

              // SVG 无障碍引用允许由空格分隔多个 id。
              if (attr.name === 'aria-labelledby' || attr.name === 'aria-describedby') {
                next = next
                  .split(/\s+/)
                  .map(function (refId) {
                    return idMap[refId] || refId;
                  })
                  .join(' ');
              }

              if (next !== value) attr.value = next;
            });
          });

          svg.setAttribute('data-vl-svg-scoped', prefix.slice(0, -1));
        });
      } catch (e) {
        /* SVG 命名空间失败不应阻断 Hero 的轮播和视频播放 */
      }
    }

    _bindControls() {
      var self = this;

      Array.prototype.forEach.call(this.querySelectorAll('[data-vl-prev]'), function (btn) {
        btn.addEventListener('click', function () {
          self.goTo(self.index - 1, { reset: true });
        });
      });

      Array.prototype.forEach.call(this.querySelectorAll('[data-vl-next]'), function (btn) {
        btn.addEventListener('click', function () {
          self.goTo(self.index + 1, { reset: true });
        });
      });

      this._prevBtn = this.querySelector('[data-vl-prev]');
      this._nextBtn = this.querySelector('[data-vl-next]');

      /* 整卡点击层（用户 2026-10-12）：滑动切换后短时间内抑制链接跳转，避免"划一下"被当成点击 */
      Array.prototype.forEach.call(this.querySelectorAll('.vl-hero__slide-link'), function (link) {
        link.addEventListener('click', function (ev) {
          if (Date.now() < (self._suppressNavClickUntil || 0)) {
            ev.preventDefault();
            ev.stopPropagation();
          }
        });
      });

      Array.prototype.forEach.call(this.querySelectorAll('[data-vl-goto]'), function (btn) {
        btn.addEventListener('click', function () {
          var target = parseInt(btn.dataset.vlGoto, 10);
          if (!isNaN(target)) self.goTo(target, { reset: true });
        });
      });
    }

    /* --------------------------------------------------------------- 渲染 */

    _render() {
      try {
        this._syncFills();
        // 到头（第一屏/最后一屏）箭头视觉变淡（设计 1411:16310 opacity-50）
        if (this._prevBtn) this._prevBtn.classList.toggle('is-disabled', this.index === 0);
        if (this._nextBtn) this._nextBtn.classList.toggle('is-disabled', this.index === this.count - 1);
      } catch (e) {
        /* noop */
      }
    }

    /* 白色填充的同步（用户 2026-10-12 第二轮反馈：「切换是闪的效果，改成滑动变化」）：
       · 稳态（未在交接）：只有 active 项有填充（＝进度 × 展开宽）；**非 active 一律清 0**
         （否则连续快速切换会残留一小段白色，圆点看起来像"变长、播放了一丢丢"）。
       · 交接中：白色随浮点位置 `_pillPos` **交叉渐变** —— 旧位置渐隐（滑出）、新位置渐显（滑入），
         而不是跟着 index 瞬移 ⇒ 视觉上是"滑动变化"而非"闪"。 */
    _syncFills() {
      var self = this;
      var p = Math.max(0, Math.min(1, this.progress || 0));
      var aw = this._activeDotW();
      var pos = typeof this._pillPos === 'number' ? this._pillPos : this.index;
      var walking = !!this._pillRaf;
      var out = Math.floor(pos);
      var into = Math.ceil(pos);
      var f = pos - out;
      this.items.forEach(function (item, i) {
        var fill = item.querySelector('[data-vl-bar-fill]');
        if (!fill) return;
        var w;
        if (!walking || out === into) {
          w = i === self.index ? p * aw : 0;
        } else if (i === out) {
          w = (self._pillOutW || 0) * (1 - f); // 旧位置：白色渐隐（滑出）
        } else if (i === into) {
          w = p * aw * f; // 新位置：白色渐显（滑入）
        } else {
          w = 0;
        }
        fill.style.width = w.toFixed(2) + 'px';
      });
    }

    /* bot 的展开宽：PC 48（设计 1411:16309）／M 72（设计 1414:17381） */
    _activeDotW() {
      return isPcBreakpoint() ? 48 : 72;
    }

    /* 「逐格交接」：把展开位置当浮点数逐帧写宽（与子需求 05 的 bot 同一机制）
       —— 任一时刻相邻两项宽度互补 ⇒ 各点宽度之和恒定、bot 条不变长变短。 */
    _writePill(pos) {
      var aw = this._activeDotW();
      this.items.forEach(function (item, i) {
        var dot = item.querySelector('.vl-hero__dot');
        if (!dot) return;
        var t = Math.min(1, Math.abs(i - pos));
        dot.style.width = (aw - (aw - DOT_BASE) * t).toFixed(2) + 'px';
      });
      this._syncFills();
    }

    _animatePill(target, options) {
      var self = this;
      if (this._pillRaf) {
        window.cancelAnimationFrame(this._pillRaf);
        this._pillRaf = 0;
      }
      if (!this.count) return;
      var from = typeof this._pillPos === 'number' ? this._pillPos : target;
      var total = Math.abs(target - from);
      var instant = (options && options.instant) || prefersReducedMotion();
      if (instant || typeof window.requestAnimationFrame !== 'function' || total < 0.001) {
        this._pillPos = target;
        this._writePill(target);
        return;
      }
      var dir = target > from ? 1 : -1;
      var dur = total * PILL_STEP_MS;
      var t0 = 0;
      var step = function (ts) {
        if (!t0) t0 = ts;
        var p = Math.min(1, (ts - t0) / dur);
        if (p >= 1) {
          self._pillPos = target;
          self._pillRaf = 0; // 先落 rAF 再写宽度：_writePill 内部按"稳态"处理填充
          self._pillOutW = 0;
          self._writePill(target);
          return;
        }
        var x = total * p;
        var idx = Math.floor(x);
        var frac = x - idx;
        var pos = from + dir * (idx + frac * frac * (3 - 2 * frac)); // 每格各自 ease-in-out
        self._pillPos = pos;
        self._writePill(pos);
        self._pillRaf = window.requestAnimationFrame(step);
      };
      this._pillPos = from;
      this._pillRaf = window.requestAnimationFrame(step);
    }

    /* ------------------------------------------------------------ 切换幻灯片 */

    goTo(target, options) {
      try {
        options = options || {};
        if (!this.count) return;
        var next = ((target % this.count) + this.count) % this.count;
        var self = this;

        // 快照旧位置的白填充宽度：交接期间用于"滑出"渐变（否则白色会瞬移 ⇒ 视觉上像闪）
        var curItem = this.items[this.index];
        var curFill = curItem ? curItem.querySelector('[data-vl-bar-fill]') : null;
        this._pillOutW = curFill ? parseFloat(curFill.style.width || '0') || 0 : 0;

        this.index = next;
        this.progress = 0;
        this._elapsed = 0;
        this._lastTs = 0;

        this.slides.forEach(function (slide, k) {
          var active = k === next;
          if (active) {
            slide.setAttribute('data-vl-active', 'true');
            slide.setAttribute('aria-hidden', 'false');
          } else {
            slide.removeAttribute('data-vl-active');
            slide.setAttribute('aria-hidden', 'true');
          }

          var shownVideo = self._activeVideoIn(slide);
          Array.prototype.forEach.call(slide.querySelectorAll('video'), function (video) {
            try {
              // 多屏视频必须用 ended 切下一屏；只有单视频才独立循环。
              video.loop = !!(active && self.singleVideoAutoplay);
              if (shownVideo && video !== shownVideo) {
                // 该断点未显示的备用视频（PC/M 各一支）：只暂停，**不 play、不 seek**
                video.pause();
                return;
              }
              /* 手机端（未启用悬停暂停）不受 `_paused` 影响：切屏后必须播放新视频（用户 2026-10-20）。
                 `currentTime = 0` ⇒ 新视频从他自己的 0 开始；非 active 的屏在上面分支已 `currentTime = 0`。 */
              if (
                active &&
                self._videoAutoplayEnabled &&
                (!self._paused || !self._hoverPauseEnabled) &&
                !options.silent
              ) {
                video.currentTime = 0;
                var played = video.play();
                if (played && played.catch) played.catch(function () {});
              } else {
                video.pause();
                if (!active) video.currentTime = 0;
              }
            } catch (e) {
              /* noop */
            }
          });
        });

        this.items.forEach(function (item, k) {
          if (k === next) item.setAttribute('data-vl-active', 'true');
          else item.removeAttribute('data-vl-active');
        });

        this._render();
        this._animatePill(next); // bot 逐格交接过去（途经中间圆点），而非直接跳到目标

        /* 切屏后按钮组的可见性/宽度可能变化（隐藏屏的测量结果为 0）⇒ 重新同步「同排/换行」判定 */
        if (window.VLButtonGroup && window.VLButtonGroup.sync) window.VLButtonGroup.sync();
      } catch (e) {
        /* noop */
      }
    }

    next() {
      this.goTo(this.index + 1, { reset: true });
    }

    prev() {
      this.goTo(this.index - 1, { reset: true });
    }

    /* ------------------------------------------------------------ 帧循环 */

    _tick(now) {
      if (this._rafId) this._rafId = requestAnimationFrame(this._tickBound);
      if (this._failed || !this.autoplay) return;

      var dt = this._lastTs ? now - this._lastTs : 0;
      this._lastTs = now;
      if (dt < 0) dt = 0;
      if (dt > 1000) dt = 1000; // 后台标签/长任务后避免跳过整屏

      if ((this._paused && this._hoverPauseEnabled) || !this._visible) {
        this._pausedVideos(true);
        return;
      }

      var slide = this.slides[this.index];
      if (!slide) return;

      var duration = parseFloat(slide.dataset.vlDuration);
      if (!isFinite(duration) || duration <= 0) duration = 5;

      var p;
      if (slide.dataset.vlMedia === 'video') {
        var video = this._activeVideoIn(slide); /* 只读当前断点显示中的那支（M 端曾误读 PC 那支） */
        var usable = video && isFinite(video.duration) && video.duration > 0 && (!video.paused || video.ended);
        if (usable) {
          p = video.ended ? 1 : video.currentTime / video.duration;
        } else if (this._elapsed >= FALLBACK_MS) {
          // 自动播放被浏览器阻止或视频尚未就绪 → 按图片时长兜底
          p = this._elapsed / (duration * 1000);
          if (!video || (video.paused && !video.ended)) {
            var resumed = video && video.play();
            if (resumed && resumed.catch) resumed.catch(function () {});
          }
        } else {
          p = 0;
        }
      } else {
        p = this._elapsed / (duration * 1000);
      }

      this._elapsed += dt;
      this.progress = Math.max(0, Math.min(1, p));
      this._render();

      if (this.progress >= 1) this.next();
    }

    /* 只处理「当前断点显示中」的那支视频：被隐藏的另一支始终保持暂停（白下流量的问题见 isPcBreakpoint 注释） */
    _pausedVideos(pause) {
      var self = this;
      try {
        this.slides.forEach(function (slide) {
          var video = self._activeVideoIn(slide);
          if (!video) return;
          if (pause) {
            video.pause();
          } else if (!video.ended) {
            var played = video.play();
            if (played && played.catch) played.catch(function () {});
          }
        });
      } catch (e) {
        /* noop */
      }
    }

    /* 取某元素内「当前断点显示中」的那支视频（找不到对应类名时兜底第一支） */
    _activeVideoIn(scope) {
      var videos = Array.prototype.slice.call(scope.querySelectorAll('video'));
      if (!videos.length) return null;
      var want = isPcBreakpoint() ? 'media__image--desktop' : 'media__image--mobile';
      for (var i = 0; i < videos.length; i++) {
        if (videos[i].classList && videos[i].classList.contains(want)) return videos[i];
      }
      return videos[0];
    }

    /* 断点切换（PC↔M）后的对齐：显示中的那支变了 ⇒ 暂停其余、把当前时间接过来继续播（同一支片源的两个编码） */
    _handleBreakpoint() {
      var self = this;
      try {
        this.slides.forEach(function (slide, i) {
          var videos = Array.prototype.slice.call(slide.querySelectorAll('video'));
          if (videos.length < 2) return;
          var shown = self._activeVideoIn(slide);
          var hidden = null;
          videos.forEach(function (v) {
            if (v === shown) return;
            hidden = v;
            try {
              v.pause();
            } catch (e) {
              /* noop */
            }
          });
          if (!shown) return;
          if (hidden && isFinite(hidden.currentTime) && !shown.ended) {
            try {
              shown.currentTime = hidden.currentTime; // 保持画面连续（两个编码同一支片）
            } catch (e) {
              /* noop */
            }
          }
          /* 播放门禁与 `goTo` 一致；单视频切换 PC/M 素材后也要继续循环播放。 */
          if (i === self.index && self._videoAutoplayEnabled && !self._paused && self._visible !== false) {
            var played = shown.play();
            if (played && played.catch) played.catch(function () {});
          } else {
            try {
              shown.pause();
            } catch (e) {
              /* noop */
            }
          }
        });
        // 断点变化后展开宽不同（PC 48 / M 72）⇒ 立刻按新宽度落位，不经过渡
        this._animatePill(this.index, { instant: true });
        this._render();
      } catch (e) {
        /* noop */
      }
    }

    /* ------------------------------------------------------------ 交互与暂停 */

    _handleVisibility() {
      this._visible = !document.hidden;
      if (!this._visible) {
        this._pausedVideos(true);
      } else if (this._videoAutoplayEnabled && !this._paused) {
        this._pausedVideos(false);
      }
    }

    _handleEnter() {
      if (!this._hoverPauseEnabled) return; // 手机端（无 hover）：不暂停
      this._paused = true;
    }

    _handleLeave() {
      if (!this._hoverPauseEnabled) return; // 手机端：未启用悬停暂停，无需恢复
      this._paused = false;
      this._lastTs = 0;
      if (this._visible && this._videoAutoplayEnabled) this._pausedVideos(false);
    }

    _handlePointerDown(event) {
      this._pointerStartX = event.clientX;
      this._pointerStartY = event.clientY;
    }

    _handlePointerUp(event) {
      if (this._pointerStartX === null || typeof this._pointerStartX === 'undefined') return;
      var dx = event.clientX - this._pointerStartX;
      var dy = event.clientY - this._pointerStartY;
      this._pointerStartX = null;
      if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy)) return;
      this._suppressNavClickUntil = Date.now() + 400; // 抑制随后的整卡点击跳转
      if (dx < 0) this.next();
      else this.prev();
    }

    _handleSectionLoad(event) {
      try {
        if (event && event.target && event.target.contains(this) === false && event.target !== this) return;
      } catch (e) {
        /* noop */
      }
    }

    _handleBlockSelect(event) {
      try {
        var blockId = event && event.detail ? event.detail.blockId : null;
        if (!blockId) return;
        var slide = this.querySelector('[data-vl-slide][data-vl-block-id="' + blockId + '"]');
        if (slide) this.goTo(parseInt(slide.dataset.vlIndex, 10) || 0, { silent: true });
      } catch (e) {
        /* noop */
      }
    }
  }

  if (!customElements.get('vl-hero-slider')) {
    customElements.define('vl-hero-slider', VLHeroSlider);
  }

  window.VLHeroSlider = { element: VLHeroSlider, fallbackMs: FALLBACK_MS, dotBase: DOT_BASE };
})();
